
import React, { useEffect, useCallback } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useInactivityLogout } from "@/hooks/security/useInactivityLogout.ts";
import InactivityWarningPopup from "@/components/common/InactivityWarningPopup";
import { MacOSNotificationManager } from "@/components/common/MacOSNotification";
import { useMacOSNotifications } from "@/hooks/system/useMacOSNotifications.ts";
import AppRoutes from "@/Routes";

const queryClient = new QueryClient();

const AppContent = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { notifications, removeNotification } = useMacOSNotifications();

  // Only enable inactivity logout for authenticated users
  const inactivityTimeout = import.meta.env.VITE_INACTIVITY_TIMEOUT ?
    parseInt(import.meta.env.VITE_INACTIVITY_TIMEOUT, 10) :
    2 * 60 * 1000; // Default: 2 minutes for testing

  const warningDuration = import.meta.env.VITE_WARNING_DURATION ?
    parseInt(import.meta.env.VITE_WARNING_DURATION, 10) :
    30 * 1000; // Default: 30 seconds for testing

  const { showWarning, countdown, dismissWarning } = useInactivityLogout({
    onLogout: logout,
    inactivityTime: inactivityTimeout,
    warningTime: warningDuration,
    enabled: !!user, // Only start timer when user is logged in
  });

  // Memoized handler for hboxViewProfile event - navigate is stable in React Router v6
  const handleViewProfile = useCallback((event: CustomEvent<{ patientId: number | string; fromGlobalSearch?: boolean; isGlobal?: boolean }>) => {
    const { patientId } = event.detail;
    console.log('📱 hboxViewProfile event received, navigating to patient:', patientId);
    if (patientId) {
      // Navigate to ES dashboard with patient ID (same pattern as GlobalSearch)
      navigate(`/es-dashboard?from=incoming-call&patientId=${patientId}`, {
        state: { 
          patientId: patientId,
          fromIncomingCall: true 
        }
      });
    }
  }, [navigate]);

  // Listen for hboxViewProfile custom event - registers once on mount
  useEffect(() => {
    window.addEventListener('hboxViewProfile', handleViewProfile as EventListener);
    console.log('🎧 hboxViewProfile event listener registered');

    return () => {
      window.removeEventListener('hboxViewProfile', handleViewProfile as EventListener);
    };
  }, [handleViewProfile]);

  return (
    <>
      {/* macOS-style Notifications */}
      <MacOSNotificationManager
        notifications={notifications}
        onRemove={removeNotification}
      />

      {/* Inactivity Warning Popup - only show for authenticated users */}
      {user && (
        <InactivityWarningPopup
          isVisible={showWarning}
          countdown={countdown}
          onDismiss={dismissWarning}
        />
      )}

      <AppRoutes />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Sentry.ErrorBoundary fallback={({ error }) => (
            <div className="min-h-screen flex items-center justify-center bg-gray-50" id="error-boundary">
              <div className="text-center p-8">
                <h1 className="text-2xl font-bold text-red-600 mb-4" id="error-title">Something went wrong</h1>
                <p className="text-gray-600 mb-4" id="error-message">We've been notified about this error.</p>
                <button
                  id="reload-button"
                  onClick={() => window.location.reload()}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                >
                  Reload Page
                </button>
              </div>
            </div>
          )}>
            <AppContent />
          </Sentry.ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
